import { OPERATIONS, ReportingTypeEnum } from '@quadminds/shared/commons';
import { TemperatureStatusEnum } from '@quadminds/shared/tracking-system';
import { IAccountOutputDto } from './account-output-dto.interface';
import { ICarrierOutputDto } from './carrier-output-dto.interface';
import { ICertificationOutputDto } from './certification-output-dto.interface';
import { IDriverOutputDto } from './driver-output-dto.interface';
import { ILocationOutputDto } from './location-output-dto.interface';
import { ITripLogOutputDto } from './log-output-dto.interface';
import { IOrderOutputDto } from './order-output-dto.interface';
import { IRouteOutputDto } from './route-output-dto.interface';
import { ISemitrailerOutputDto } from './semi-output-dto.interface';
import { ITripServiceOutputDto } from './service-type-dto.interface';
import { ITagOutputDto } from './tags-output-dto.interface';
import { IVehicleOutputDto } from './vehicle-output-dto.interface';
import {
    CheckpointOperationType,
    TripErrorCodeEnum,
    TripFinishedStatusEnum,
    TripWarningCodeEnum,
    VisitStatus,
    StepOperationType,
} from '../enums';

export interface ITripEtaStopDto {
    start: string;
    finish: string;
    type?: 'continuous' | 'daily' | 'window';
}

export interface ITripOutputDto {
    id: string;
    organizationId: string;
    realStart: string;
    checkpoints: ICheckpointOutputDto[];
    tripDefinition: string;
    delayedYellow: string;
    delayedRed: string;
    pendingColor: string;
    finishedColor: string;
    scheduledStart: string;
    scheduledFinish: string;
    realFinish: string;
    finishReason: string;
    finishBy: string;
    locale: string;
    tripLifetime: string;
    tripStartedLifetime: string;
    vehicle: string | IVehicleOutputDto;
    semitrailers?: (string | ISemitrailerOutputDto)[];
    tripCode: string;
    driver: string | IDriverOutputDto;
    /** Second person on board (helper). Never carries the trip's kilometers. */
    companion?: string | IDriverOutputDto | null;
    totalDelay: number;
    carrier?: string | ICarrierOutputDto;
    originalUnit?: ITripOriginalUnitOutputDto;
    unfold?: ITripUnfoldDto;
    terminalName: string;
    accounts?: Array<string | IAccountOutputDto>;
    meters: number;
    metersLastUpdatedAt?: string;
    theoreticalMeters?: number;
    minPercentage: number;
    maxPercentage: number;
    maxDrivingTime?: number;
    restingTime?: number;
    maxDailyDrivingTime?: number;
    drivingTimeWindowTolerance?: number;
    metersWithoutOptions: number;
    finishOnLastCheckpointStep: boolean;
    forceShowLastCheckpointNext: boolean;
    stopped: boolean;
    stoppedTimeIntransitToFirstCheckpoint?: number;
    drivenTime?: number;
    drivingTimeWindow?: [string, string][];
    triggerOrder?: ITriggerOutputDto[];
    ttlFinishedTrips?: string;
    alerts?: string[];
    logs?: (string | ITripLogOutputDto)[];
    originStateDefinition?: string;
    routingErrors?: IRoutingErrorOutputDto[];
    reportingType: ReportingTypeEnum;
    tags?: string[] | ITagOutputDto[];
    rateId?: string;
    services?: ITripServiceOutputDto[];
    errors?: ITripErrorsDto[];
    isDeleted?: boolean;
    deletedAt?: string;
    createdAt?: string;
    updatedAt?: string;
    anticipationTime?: string;
    completeLastCheckpointStepWhenTriggered?: boolean;
    finishTripsInProgress?: boolean;
    skipEstimation?: boolean;
    finishStateDefinition?: string;
    status?: TripFinishedStatusEnum;
    temperature?: {
        value: number;
        lastUpdatedAt: string;
        status: TemperatureStatusEnum;
    };
    etaContext?: {
        stops?: ITripEtaStopDto[];
    };
    skipRouting?: boolean;
    startNextTripOnPrevFinish?: boolean;
    updatedBy?: { userId: string; userName: string; timestamp: Date; updatedFields?: string[] }[];
}

export interface ITriggerOutputDto {
    checkpointIndex: number;
    stepIndex: number;
}

export interface ICheckpointOrderDto {
    order: string | IOrderOutputDto;
    code: string;
    certification: string | ICertificationOutputDto;
    label: string;
    operation: OPERATIONS;
}

export interface ITripErrorsDto {
    code: TripErrorCodeEnum;
    message: string;
    metadata: Record<string, unknown>;
}

export interface ITripWarningDto {
    code: TripWarningCodeEnum;
    message: string;
    metadata?: Record<string, unknown>;
}

export interface ICheckpointOutputDto {
    checkpointId?: string;
    type: string;
    steps: IStepOutputDto[];
    checkpointDefinitionId: string;
    stateDefinition: string;
    transitionStateDefinition?: string;
    name: string;
    location: string | ILocationOutputDto;
    visitStatus: VisitStatus;
    dwellTime?: number;
    orders?: ICheckpointOrderDto[];
    certification?: string | ICertificationOutputDto;
    operationType?: CheckpointOperationType;
    createdByOrganizationId?: string;
    traveledMetersAfter?: number;
    theoreticalMetersAfter?: number;
    latitude?: string;
    longitude?: string;
    address?: string;
    isPlanned?: boolean;
    expectedRouteToArrivePolyline?: string;
    route?: string | IRouteOutputDto;
    account?: string | IAccountOutputDto;
    unitChange?: ICheckpointUnitChangeOutputDto;
    mirror?: ICheckpointMirrorDto;
}
export interface ITripUnfoldDto {
    hubCheckpointType: string;
    tripIds?: string[];
}

export interface ICheckpointMirrorDto {
    tripId: string;
}

/**
 * The unit that ran section 1 ("tramo 1"). The trip root holds the unit *currently* in charge — it is
 * overwritten when a desenganche completes — so this is what every section fold must use as its
 * header. Absent means the trip never handed over, in which case the root still is section 1.
 */
export interface ITripOriginalUnitOutputDto {
    carrier?: string | ICarrierOutputDto;
    vehicle?: string | IVehicleOutputDto;
    driver?: string | IDriverOutputDto;
    semitrailers?: (string | ISemitrailerOutputDto)[];
}

/**
 * When each unit was at the handover stop. Observed, never authored: the arrivals and departures of
 * the unit in charge come from the anchor's own steps, and the other unit's from its positions.
 * Absent means "not observed", which is true for every trip that predates this.
 */
export interface ICheckpointHandoverDto {
    outgoingArrivedAt?: string;
    outgoingLeftAt?: string;
    incomingArrivedAt?: string;
    incomingLeftAt?: string;
    completedAt?: string;
}

export interface ICheckpointUnitChangeOutputDto {
    carrier?: string | ICarrierOutputDto;
    vehicle?: string | IVehicleOutputDto;
    driver?: string | IDriverOutputDto;
    semitrailers?: (string | ISemitrailerOutputDto)[];
    changedAt?: string;
    handover?: ICheckpointHandoverDto;
}

export interface IStepOutputDto {
    plannedAt?: string;
    completedAt?: string;
    eta?: string;
    normalDuration: string;
    type: string;
    icon: string;
    name: string;
    finishTripsInProgress: boolean;
    maxDuration: string;
    minDuration: string;
    action?: string;
    slug?: string;
    zones?: string[];
    overwritePlanned?: string;
    estimatedAt?: string;
    triggeredAt?: string;
    stateDefinition?: string;
    triggerWithCertification?: boolean;
    finishTripAfter?: string;
    operationType?: StepOperationType;
    isNonOperative?: boolean;
    manualTrigger?: boolean;
}

export interface IRoutingErrorOutputDto {
    locationId: string;
    locationName: string;
    latitude: string;
    longitude: string;
    timestamp: string;
    calculationType: string;
}
